import React, { useRef, useState } from "react";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import { FaDownload, FaTrash, FaArrowLeft } from "react-icons/fa";
import styles from "./SalarySlip.module.css";
import { fmtINR } from "../utils/salaryUtils";

const convertToWordsINR = (num) => {
  if (num === null || num === undefined) return "N/A";
  const a = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
           "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen",
           "Seventeen", "Eighteen", "Nineteen"];
  const b = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
  const inWords = (n) => {
    let str = "";
    if (n > 99) { str += a[Math.floor(n / 100)] + " Hundred "; n %= 100; }
    if (n > 19) { str += b[Math.floor(n / 10)] + " " + a[n % 10] + " "; }
    else if (n > 0) { str += a[n] + " "; }
    return str;
  };
  let numStr = Math.floor(num).toString();
  let paise = Math.round((num - Math.floor(num)) * 100);
  let crore = numStr.slice(-9, -7) || "0";
  let lakh = numStr.slice(-7, -5) || "0";
  let thousand = numStr.slice(-5, -3) || "0";
  let hundreds = numStr.slice(-3) || "0";
  let words = "";
  if (parseInt(crore)) words += inWords(parseInt(crore)) + "Crore ";
  if (parseInt(lakh)) words += inWords(parseInt(lakh)) + "Lakh ";
  if (parseInt(thousand)) words += inWords(parseInt(thousand)) + "Thousand ";
  if (parseInt(hundreds)) words += inWords(parseInt(hundreds));
  if (words === "") words = "Zero";
  let result = "Rupees " + words.trim() + " ";
  if (paise > 0) result += "and " + inWords(paise).trim() + " Paise ";
  return result.replace(/\s+/g, " ").trim() + " Only";
};

const LEAVE_SUMMARY_KEYS = [
  { key: "EL", label: "EL", defaultDays: 12 },
  { key: "CL", label: "CL", defaultDays: 6 },
  { key: "SL", label: "SL", defaultDays: 6 },
  { key: "PL", label: "PL", defaultDays: 3 },
  { key: "BL", label: "BL", defaultDays: 2 },
];

const SalarySlip = ({ slip, employee, onBack, onDelete, isDeletable, isDeleting, breakdown, leaveBalance, leaveYear }) => {
  const slipRef = useRef();
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownloadPDF = () => {
    const input = slipRef.current;
    if (!input) return;
    setIsDownloading(true);
    html2canvas(input, { scale: 2, useCORS: true }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
      pdf.save(`Payslip-${employee.employeeId}-${slip.month}.pdf`);
    }).finally(() => {
      setIsDownloading(false);
    });
  };

  // --- CONFIRMATION/NOTIFICATION HANDLER ADDED ---
  const handleDelete = () => {
    if (window.confirm("Are you sure you want to delete this payslip? This action cannot be undone.")) {
      onDelete(slip.id);
      // Note: A true "success" notification (e.g., "Slip deleted!") 
      // should be triggered in the parent component *after* the async onDelete operation finishes.
    }
  };
  // --- END ---

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || amount === 0) return "N/A";
    return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(amount);
  };

  const DetailItem = ({ label, value }) => (
    <div className={styles.slipDetailItem}>
      <span className={styles.slipDetailLabel}>{label}</span>
      <span className={styles.slipDetailValue}>{value || "N/A"}</span>
    </div>
  );

  const FIXED_EARNING_NAMES = ["Basic", "HRA", "LTA", "Medical Allowance", "Telephone & Internet Allowance", "Special Allowance"];
  const OTHERS_NAMES = ["Variable Pay", "Retention Bonus", "Joining Bonus", "Joining/Relocation Allowance"];

  const fixedEarnings = FIXED_EARNING_NAMES.map(name => {
    const found = slip.earnings?.find(e => e.name === name);
    return { name, amount: found ? found.amount : 0 };
  });

  const retentionInSlip = slip.earnings?.find(e => e.name === "Retention Bonus");
  const variableInSlip  = slip.earnings?.find(e => e.name === "Variable Pay");
  const joiningInSlip       = slip.earnings?.find(e => e.name === "Joining Bonus");
  const relocationInSlip    = slip.earnings?.find(e => e.name === "Joining/Relocation Allowance");

  const extraEarnings = (slip.earnings || []).filter(
    e => !FIXED_EARNING_NAMES.includes(e.name) && !OTHERS_NAMES.includes(e.name)
  );

  // ── Build flat row arrays for combined table ──────────────────────────
  const DEDUCTION_NAMES = ["Professional Tax", "Provident Fund (EPF)", "Income Tax (TDS)"];

  const earningRows = [
    ...FIXED_EARNING_NAMES.map(name => {
      const found = slip.earnings?.find(e => e.name === name);
      return { name, amount: found?.amount ?? null };
    }),
    // Joining / Relocation Allowance (always shown, — when not applicable)
    { name: 'Joining/Relocation Allowance', amount: relocationInSlip?.amount ?? null },
    // Variable Pay
    ...(breakdown?.hasVariable || variableInSlip
      ? [{ name: 'Variable Pay', amount: variableInSlip?.amount ?? null }]
      : []),
    // Retention Bonus
    ...(breakdown?.hasRetention || retentionInSlip
      ? [{ name: 'Retention Bonus', amount: retentionInSlip?.amount ?? null }]
      : []),
    // Joining Bonus / Others
    ...(breakdown?.joiningBonus > 0 || joiningInSlip
      ? [{ name: 'Others', amount: joiningInSlip?.amount ?? null }]
      : []),
    // Any extra ad-hoc earnings
    ...extraEarnings.map(e => ({ name: e.name, amount: e.amount })),
  ];

  // Extra deductions beyond the standard three (shown with their actual label)
  const extraDeductionItems = (slip.deductions || []).filter(
    d => !DEDUCTION_NAMES.includes(d.name)
  );

  const deductionRows = [
    ...DEDUCTION_NAMES.map(name => {
      const found = slip.deductions?.find(d => d.name === name);
      return { name, amount: found?.amount ?? null };
    }),
    ...extraDeductionItems.map(d => ({ name: d.name, amount: d.amount })),
  ];

  const totalRows = Math.max(earningRows.length, deductionRows.length);

  const allDeductions = DEDUCTION_NAMES.map(name => {
    const found = slip.deductions?.find(d => d.name === name);
    return { name, amount: found ? found.amount : 0 };
  });

  return (
    <div className={styles.salarySlipContainer}>
      <div className={styles.slipActions}>
        <button onClick={onBack} className={styles.actionBtnSecondary} data-tooltip="Back to Salary List"><FaArrowLeft /> Back</button>
        <div>
          <button onClick={handleDownloadPDF} className={styles.actionBtnPrimary} disabled={isDownloading} data-tooltip="Download Salary Slip as PDF">
            {isDownloading
              ? <><span className={styles.btnSpinner} /> Generating…</>
              : <><FaDownload /> Download PDF</>}
          </button>
          {isDeletable && (
            // --- onClick HANDLER UPDATED ---
            <button onClick={handleDelete} className={styles.actionBtnDanger} disabled={isDeleting} data-tooltip="Delete Salary Slip">
              {isDeleting ? "Deleting..." : <><FaTrash /> Delete Slip</>}
            </button>
            // --- END ---
          )}
        </div>
      </div>

      <div ref={slipRef} className={styles.slipContainer}>
        <div className={styles.slipCompanyHeader}>
          <div className={styles.slipLogo}>
            <img src="/PrepXPathshala_logo2.jpg" alt="Company Logo" />
          </div>
          <div className={styles.slipCompanyInfo}>
            <h1>PRREPX PATHSHALA PRIVATE LIMITED</h1>
            <p style={{fontSize: '0.8em', marginTop: '2px'}}>ET 15/302 EWS LIG, DLF GARDEN, Purseni, Mohanlalganj, Lucknow- 226301 Uttar Pradesh</p>
            <p style={{fontSize: '0.85em', marginTop: '4px'}}>CIN: U85306UP2025PTC238181 | Website: https://www.prrepxpathshala.live</p>
            <p style={{fontWeight: 'bold', marginTop: '8px'}}>Payslip for {slip.month} {slip.year}</p>
          </div>
        </div>

        <div className={styles.slipEmployeeDetails}>
          <h4>Employee Information</h4>
          <div className={styles.slipEmployeeGrid}>
            <div>
              <DetailItem label="Name" value={employee.name} />
              <DetailItem label="Employee Code" value={employee.employeeId} />
              <DetailItem label="Department" value={employee.department} />
              <DetailItem label="Date of Commencement" value={employee.joiningDate} />
              <DetailItem label="Location" value={employee.location} />
              <DetailItem label="PF No." value={employee.pfNumber} />
            </div>
            <div>
              <DetailItem label="Position" value={employee.position} />
              <DetailItem label="Bank Name" value={employee.bankName} />
              <DetailItem label="Bank A/C No." value={employee.bankAccountNumber} />
              <DetailItem label="PAN No." value={employee.panNumber} />
              <DetailItem label="UAN" value={employee.uanNumber} />
            </div>
          </div>
        </div>

        {/* ── Combined Earnings + Deductions table ─────────────── */}
        <table className={styles.slipCombinedTable}>
          <colgroup>
            <col style={{width:'38%'}} /><col style={{width:'12%'}} />
            <col style={{width:'38%'}} /><col style={{width:'12%'}} />
          </colgroup>
          <thead>
            <tr>
              <th colSpan="2" className={styles.slipGroupHeader}>Earnings</th>
              <th colSpan="2" className={styles.slipGroupHeader}>Deductions</th>
            </tr>
            <tr>
              <th>Description</th>
              <th className={styles.amt}>Amount</th>
              <th>Description</th>
              <th className={styles.amt}>Amount</th>
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: totalRows }, (_, i) => {
              const e = earningRows[i];
              const d = deductionRows[i];
              return (
                <tr key={i}>
                  <td>{e?.name ?? ''}</td>
                  <td className={styles.amt}>
                    {e ? (e.amount != null && e.amount !== 0 ? formatCurrency(e.amount) : '\u2014') : ''}
                  </td>
                  <td>{d?.name ?? ''}</td>
                  <td className={styles.amt}>
                    {d ? (d.amount != null && d.amount !== 0 ? formatCurrency(d.amount) : '\u2014') : ''}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className={styles.slipTotalRow}>
              <td>Total Earning:</td>
              <td className={styles.amt}>{formatCurrency(slip.grossSalary)}</td>
              <td>Total Deduction:</td>
              <td className={styles.amt}>{formatCurrency(slip.totalDeductions)}</td>
            </tr>
            <tr className={styles.slipNetRow}>
              <td>Monthly Salary:</td>
              <td className={styles.amt}>{formatCurrency(slip.grossSalary)}</td>
              <td><strong>Net Pay:</strong></td>
              <td className={styles.amt}><strong>{formatCurrency(slip.netSalary)}</strong></td>
            </tr>
          </tfoot>
        </table>

        <div className={styles.slipNetWords}>
          <strong>Net Pay In Words:</strong> {convertToWordsINR(slip.netSalary)}
        </div>

        {/* ── Leave Balance Summary ──────────────────────────────── */}
        <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 14, fontSize: "0.78rem" }}>
          <thead>
            <tr>
              <th style={{ border: "1px solid #aaa", padding: "5px 10px", textAlign: "left", background: "#f1f5f9", fontWeight: 700 }}>Leaves ({leaveYear})</th>
              {LEAVE_SUMMARY_KEYS.map(({ key, label }) => (
                <th key={key} style={{ border: "1px solid #aaa", padding: "5px 12px", textAlign: "center", background: "#f1f5f9", fontWeight: 700 }}>{label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ border: "1px solid #aaa", padding: "4px 10px", fontWeight: 600 }}>Leave C/F &amp; Assigned</td>
              {LEAVE_SUMMARY_KEYS.map(({ key, defaultDays }) => (
                <td key={key} style={{ border: "1px solid #aaa", padding: "4px 12px", textAlign: "center" }}>
                  {leaveBalance?.[key]?.total ?? defaultDays}
                </td>
              ))}
            </tr>
            <tr>
              <td style={{ border: "1px solid #aaa", padding: "4px 10px", fontWeight: 600 }}>Leave Availed</td>
              {LEAVE_SUMMARY_KEYS.map(({ key }) => (
                <td key={key} style={{ border: "1px solid #aaa", padding: "4px 12px", textAlign: "center" }}>
                  {leaveBalance?.[key]?.taken ?? 0}
                </td>
              ))}
            </tr>
            <tr style={{ background: "#f8fafc" }}>
              <td style={{ border: "1px solid #aaa", padding: "4px 10px", fontWeight: 800 }}>Leave Balance</td>
              {LEAVE_SUMMARY_KEYS.map(({ key, defaultDays }) => {
                const total = leaveBalance?.[key]?.total ?? defaultDays;
                const taken = leaveBalance?.[key]?.taken ?? 0;
                const pending = leaveBalance?.[key]?.pending ?? 0;
                const remaining = total - taken - pending;
                return (
                  <td key={key} style={{ border: "1px solid #aaa", padding: "4px 12px", textAlign: "center", fontWeight: 800, color: remaining <= 0 ? "#dc2626" : remaining <= 2 ? "#d97706" : "#000" }}>
                    {remaining}
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>

        <div className={styles.slipFooter}>
          <p>This is a computer-generated payslip and does not require a signature.</p>
        </div>
      </div>
    </div>
  );
};

export default SalarySlip;